/**
 * RichTextEditor — TipTap block editor (merged from the KB editor).
 * Markdown/MDX in, Markdown/MDX out. Real nodes for tables, columns, cards,
 * steps, accordions, callouts, embeds; anything it does not model is kept
 * verbatim as raw MDX nodes (see mdxBridge.js) so nothing is lost on save.
 */
import { useEditor, EditorContent } from '@tiptap/react';
import { generateJSON } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TableMd, TableCellMd, TableHeaderMd } from './extensions/TableMd';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { TableRow } from '@tiptap/extension-table-row';
import { Markdown } from 'tiptap-markdown';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Bold, Italic,
  List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImageIcon,
  Undo2, Redo2, ChevronDown, Plus,
  Info, Lightbulb, AlertTriangle, CheckCircle, AlertCircle, MoreHorizontal,
  Columns, Youtube, Minus, TableIcon, Video, Globe
} from 'lucide-react';
import { SlashCommand } from './extensions/SlashCommand';
import ImagePickerModal from '../ImagePickerModal';
import { ColumnsBlockNode, ColumnCardNode } from './extensions/ColumnsBlock';
import { IframeEmbed } from './extensions/IframeEmbed';
import { StepsBlockNode, StepItemNode } from './extensions/StepsBlock';
import { ColumnLayoutNode, ColumnPaneNode } from './extensions/ColumnLayout';
import { AccordionBlockNode, AccordionItemNode } from './extensions/AccordionBlock';
import { CalloutBlockNode } from './extensions/CalloutBlock';
import { TableRowMenu } from './extensions/TableMenu';
import { RawMdxBlock, RawInline } from './extensions/RawMdxBlock';
import { EmbedBlock } from './extensions/EmbedBlock';
import { CodeBlockMeta } from './extensions/CodeBlockMeta';
import { mdToHtml } from './mdxBridge';

// Single source of truth for the schema (also used to turn our HTML into a
// ProseMirror doc directly, bypassing tiptap-markdown's markdown-it pass).
export const buildExtensions = () => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3, 4, 5, 6] },
    codeBlock: false,
    link: false,
    underline: false,
  }),
  CodeBlockMeta,
  Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#00A1B2] underline' } }),
  Image.configure({ inline: false, allowBase64: true }),
  Underline,
  Placeholder.configure({
    placeholder: ({ node }) => {
      if (node.type.name === 'paragraph') return 'Type / for commands, or start writing...';
      return '';
    },
    includeChildren: true,
  }),
  Markdown.configure({
    html: true,
    tightLists: true,
    bulletListMarker: '-',
    transformPastedText: true,
    transformCopiedText: true,
  }),
  SlashCommand,
  ColumnsBlockNode,
  ColumnCardNode,
  IframeEmbed,
  StepsBlockNode,
  StepItemNode,
  ColumnLayoutNode,
  ColumnPaneNode,
  AccordionBlockNode,
  AccordionItemNode,
  CalloutBlockNode,
  EmbedBlock,
  RawMdxBlock,
  RawInline,
  TableMd.configure({ resizable: false, HTMLAttributes: { class: 'editor-table' } }),
  // tight (no blank line between items) like bullet/ordered lists
  TaskList.extend({
    addAttributes() {
      return { ...this.parent?.(), tight: { default: true, rendered: false } };
    },
  }),
  TaskItem.configure({ nested: true }),
  TableRow,
  TableHeaderMd,
  TableCellMd,
];

// markdown/MDX text -> ProseMirror JSON
export const mdxToDoc = (md, extensions) => {
  const html = mdToHtml(md || '');
  return generateJSON(html || '<p></p>', extensions);
};

// ============= Editor UI Components =============

const API = process.env.REACT_APP_BACKEND_URL;

const HEADING_OPTIONS = [
  { level: 0, label: 'Paragraph' },
  { level: 1, label: 'Heading 1' },
  { level: 2, label: 'Heading 2' },
  { level: 3, label: 'Heading 3' },
  { level: 4, label: 'Heading 4' },
  { level: 5, label: 'Heading 5' },
  { level: 6, label: 'Heading 6' },
];

const INSERT_ITEMS = [
  { key: 'callout_info', label: 'Info Callout', icon: <Info className="w-4 h-4 text-blue-400" />, isCallout: true, calloutType: 'info' },
  { key: 'callout_check', label: 'Check Callout', icon: <CheckCircle className="w-4 h-4 text-[#00A1B2]" />, isCallout: true, calloutType: 'check' },
  { key: 'callout_note', label: 'Note Callout', icon: <Info className="w-4 h-4 text-blue-400" />, isCallout: true, calloutType: 'note' },
  { key: 'callout_tip', label: 'Tip Callout', icon: <Lightbulb className="w-4 h-4 text-[#00A1B2]" />, isCallout: true, calloutType: 'tip' },
  { key: 'callout_warning', label: 'Warning Callout', icon: <AlertTriangle className="w-4 h-4 text-amber-400" />, isCallout: true, calloutType: 'warning' },
  { key: 'callout_danger', label: 'Danger Callout', icon: <AlertCircle className="w-4 h-4 text-red-400" />, isCallout: true, calloutType: 'danger' },
  { key: 'steps', label: 'Steps', icon: <CheckCircle className="w-4 h-4 text-emerald-400" />, isVisualSteps: true },
  { key: 'tabs', label: 'Tabs', icon: <Columns className="w-4 h-4 text-cyan-400" />, rawMdx: '<Tabs>\n<Tab title="Tab 1">\nContent\n</Tab>\n<Tab title="Tab 2">\nContent\n</Tab>\n</Tabs>' },
  { key: 'accordion', label: 'Accordion', icon: <MoreHorizontal className="w-4 h-4 text-slate-400" />, isAccordion: true },
  { key: 'youtube', label: 'YouTube Video', icon: <Youtube className="w-4 h-4 text-red-400" />, embed: 'YouTube' },
  { key: 'loom', label: 'Loom Video', icon: <Video className="w-4 h-4 text-purple-400" />, embed: 'Loom' },
  { key: 'iframe', label: 'Iframe Embed', icon: <Globe className="w-4 h-4 text-sky-400" />, iframe: true },
  { key: 'columns_1', label: '1 Column', icon: <Columns className="w-4 h-4 text-teal-400" />, isColumnLayout: true, cols: 1 },
  { key: 'columns_2', label: '2 Columns', icon: <Columns className="w-4 h-4 text-teal-400" />, isColumnLayout: true, cols: 2 },
  { key: 'columns_3', label: '3 Columns', icon: <Columns className="w-4 h-4 text-teal-400" />, isColumnLayout: true, cols: 3 },
  { key: 'code_block', label: 'Code Block', icon: <Code className="w-4 h-4 text-green-400" />, snippet: '```javascript\n// Your code here\n```' },
  { key: 'table', label: 'Table', icon: <TableIcon className="w-4 h-4 text-blue-400" />, isTable: true },
  { key: 'horizontal_rule', label: 'Horizontal Rule', icon: <Minus className="w-4 h-4 text-gray-400" />, snippet: '---' },
];

const ToolBtn = ({ onClick, active, disabled, children, title, theme }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-md transition-all ${active ? 'bg-[#00A1B2] text-white' : `${theme.textMuted} ${theme.hoverText} ${theme.hover}`} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
    data-testid={`toolbar-${title?.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {children}
  </button>
);

export const RichTextEditor = ({ content, onChange, theme, onUploadImage, projectId }) => {
  const [showInsert, setShowInsert] = useState(false);
  const [showHeading, setShowHeading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const fileInputRef = useRef(null);
  const insertRef = useRef(null);
  const headingRef = useRef(null);

  const extensions = useMemo(() => buildExtensions(), []);
  const lastExternalContent = useRef(content);

  const editor = useEditor({
    extensions,
    content: mdxToDoc(content, extensions),
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[400px] px-0 py-2',
      },
    },
    onUpdate: ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown().replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '') + '\n';
      lastExternalContent.current = md;
      onChange?.(md);
    },
  });

  // Update editor content when external content changes (switching articles)
  useEffect(() => {
    if (!editor) return;
    if (content !== lastExternalContent.current) {
      lastExternalContent.current = content;
      editor.commands.setContent(mdxToDoc(content || '', extensions), { emitUpdate: false });
    }
  }, [content, editor, extensions]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (insertRef.current && !insertRef.current.contains(e.target)) setShowInsert(false);
      if (headingRef.current && !headingRef.current.contains(e.target)) setShowHeading(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const insertSnippet = useCallback((snippet) => {
    if (!editor) return;
    editor.chain().focus().insertContent('\n\n' + snippet + '\n\n').run();
    setShowInsert(false);
  }, [editor]);

  const insertColumnLayout = useCallback((cols) => {
    if (!editor) return;
    const panes = Array.from({ length: cols }, () => ({
      type: 'columnPane',
      content: [{ type: 'paragraph' }],
    }));
    editor.chain().focus().insertContent({
      type: 'columnLayout',
      attrs: { cols },
      content: panes,
    }).run();
    setShowInsert(false);
  }, [editor]);

  const insertVisualSteps = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertContent({
      type: 'stepsBlock',
      content: [{
        type: 'stepItem',
        attrs: { title: 'Step 1' },
        content: [{ type: 'paragraph' }],
      }],
    }).run();
    setShowInsert(false);
  }, [editor]);

  const insertAccordion = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertContent({
      type: 'accordionBlock',
      content: [{
        type: 'accordionItem',
        attrs: { title: 'Section 1' },
        content: [{ type: 'paragraph' }],
      }],
    }).run();
    setShowInsert(false);
  }, [editor]);

  const insertCallout = useCallback((calloutType) => {
    if (!editor) return;
    const labels = { info: 'Info', check: 'Check', note: 'Note', tip: 'Tip', warning: 'Warning', danger: 'Danger' };
    editor.chain().focus().insertContent({
      type: 'calloutBlock',
      attrs: { calloutType, title: labels[calloutType] || 'Info' },
      content: [{ type: 'paragraph' }],
    }).run();
    setShowInsert(false);
  }, [editor]);

  const insertEmbed = useCallback((kind) => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: 'embedBlock', attrs: { kind, id: '', title: '' } }).run();
    setShowInsert(false);
  }, [editor]);

  const insertIframe = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: 'iframeEmbed', attrs: { src: '', title: '', rawHtml: '' } }).run();
    setShowInsert(false);
  }, [editor]);

  const insertRawMdx = useCallback((source) => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: 'rawMdx', attrs: { source } }).run();
    setShowInsert(false);
  }, [editor]);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    setShowInsert(false);
  }, [editor]);

  const handleImageUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/') || !onUploadImage) return;
    const url = await onUploadImage(file);
    if (url && editor) {
      editor.chain().focus().setImage({ src: url, alt: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ') }).run();
    }
  }, [editor, onUploadImage]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
    e.target.value = '';
  };

  // Image Picker (Phase 4) — Stock/GIF/Upload modal hands back finished
  // markdown (a `<Figure ... />` or `![alt](url)`, alt-text gate already
  // enforced inside the modal), inserted the same way slash-command
  // snippets are (insertSnippet above) rather than going through TipTap's
  // Image node directly — that keeps a captioned <Figure> round-tripping
  // through STANDALONE_TAGS like any other AI- or hand-authored one.
  const insertImageMarkdown = useCallback((markdown) => {
    if (!editor) return;
    if (markdown.trim().startsWith('<')) {
      editor.chain().focus().insertContent({ type: 'rawMdx', attrs: { source: markdown.trim() } }).run();
    } else {
      const m = markdown.match(/^!\[([^\]]*)\]\((.*)\)$/);
      if (m) editor.chain().focus().setImage({ src: m[2], alt: m[1] }).run();
      else editor.chain().focus().insertContent('\n\n' + markdown + '\n\n').run();
    }
    setShowImagePicker(false);
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const getCurrentHeading = () => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return `Heading ${i}`;
    }
    return 'Paragraph';
  };

  return (
    <div data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className={`flex items-center gap-1 flex-wrap py-2 mb-3 border-b ${theme.border} sticky top-0 z-20 ${theme.id === 'dark' ? 'bg-[#0a0a0a]' : 'bg-white'}`} data-testid="editor-toolbar">
        {/* Insert Button */}
        <div className="relative" ref={insertRef}>
          <button
            type="button"
            onClick={() => { setShowInsert(!showInsert); setShowHeading(false); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${showInsert ? 'bg-[#00A1B2] text-white' : `${theme.inputBg} border ${theme.inputBorder} ${theme.textMuted} ${theme.hoverText}`}`}
            data-testid="insert-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Insert
          </button>
          {showInsert && (
            <div className={`absolute z-50 top-full mt-1 left-0 ${theme.id === 'dark' ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'} border rounded-xl shadow-2xl py-1.5 w-52`} data-testid="insert-menu">
              {INSERT_ITEMS.map(item => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => item.isCallout ? insertCallout(item.calloutType) : item.isTable ? insertTable() : item.isColumnLayout ? insertColumnLayout(item.cols) : item.isVisualSteps ? insertVisualSteps() : item.isAccordion ? insertAccordion() : item.embed ? insertEmbed(item.embed) : item.iframe ? insertIframe() : item.rawMdx ? insertRawMdx(item.rawMdx) : insertSnippet(item.snippet)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm ${theme.textMuted} ${theme.hover} ${theme.hoverText} transition-colors`}
                  data-testid={`insert-${item.key}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Heading Dropdown */}
        <div className="relative" ref={headingRef}>
          <button
            type="button"
            onClick={() => { setShowHeading(!showHeading); setShowInsert(false); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all min-w-[110px] justify-between ${theme.inputBg} border ${theme.inputBorder} ${theme.textMuted} ${theme.hoverText}`}
            data-testid="heading-dropdown"
          >
            <span>{getCurrentHeading()}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {showHeading && (
            <div className={`absolute z-50 top-full mt-1 left-0 ${theme.id === 'dark' ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'} border rounded-xl shadow-2xl py-1 w-40`} data-testid="heading-menu">
              {HEADING_OPTIONS.map(opt => (
                <button
                  key={opt.level}
                  type="button"
                  onClick={() => {
                    if (opt.level === 0) editor.chain().focus().setParagraph().run();
                    else editor.chain().focus().toggleHeading({ level: opt.level }).run();
                    setShowHeading(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                    (opt.level === 0 && !editor.isActive('heading')) || editor.isActive('heading', { level: opt.level })
                      ? 'text-[#00A1B2] font-medium' : `${theme.textMuted} ${theme.hover} ${theme.hoverText}`
                  }`}
                  data-testid={`heading-${opt.level}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`w-px h-5 ${theme.divider} mx-0.5`} />

        {/* Formatting */}
        <ToolBtn onClick={setLink} active={editor.isActive('link')} title="Link" theme={theme}><LinkIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" theme={theme}><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" theme={theme}><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote" theme={theme}><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline Code" theme={theme}><Code className="w-4 h-4" /></ToolBtn>

        <div className={`w-px h-5 ${theme.divider} mx-0.5`} />

        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List" theme={theme}><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List" theme={theme}><ListOrdered className="w-4 h-4" /></ToolBtn>

        <div className={`w-px h-5 ${theme.divider} mx-0.5`} />

        <ToolBtn onClick={() => setShowImagePicker(true)} title="Insert Image" theme={theme}><ImageIcon className="w-4 h-4" /></ToolBtn>
        {/* Kept for any other caller of handleFileSelect's plumbing; no longer
            wired to a toolbar button now that Insert Image opens the picker. */}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

        <div className="ml-auto flex items-center gap-0.5">
          <ToolBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" theme={theme}><Undo2 className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" theme={theme}><Redo2 className="w-4 h-4" /></ToolBtn>
        </div>
      </div>

      {/* Slash command hint */}
      <p className={`text-[11px] ${theme.textTertiary} mb-2`}>Type <kbd className={`px-1 py-0.5 rounded text-[10px] ${theme.id === 'dark' ? 'bg-slate-800/50 border border-white/5' : 'bg-gray-100 border border-gray-200 text-gray-500'}`}>/</kbd> for commands</p>

      {/* Editor Content with Table Menu */}
      <div className="relative">
        <TableRowMenu editor={editor} theme={theme} />
        <EditorContent
          editor={editor}
          className={`tiptap-editor prose ${theme.proseClass} max-w-none ${theme.text} [&_.tiptap]:outline-none [&_.tiptap]:min-h-[400px]`}
          data-testid="editor-content-area"
        />
      </div>

      {/* Table styles for the editor — adapts to light / dark via html.dark class */}
      <style>{`
        .tiptap-editor .editor-table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
          overflow: hidden;
          border-radius: 0.5rem;
          border: 1px solid rgba(0,0,0,0.12);
        }
        .dark .tiptap-editor .editor-table {
          border: 1px solid rgba(255,255,255,0.1);
        }
        .tiptap-editor .editor-table th,
        .tiptap-editor .editor-table td {
          border: 1px solid rgba(0,0,0,0.08);
          padding: 0.5rem 0.75rem;
          position: relative;
          min-width: 100px;
          vertical-align: top;
        }
        .dark .tiptap-editor .editor-table th,
        .dark .tiptap-editor .editor-table td {
          border-color: rgba(255,255,255,0.1);
        }
        .tiptap-editor .editor-table th {
          font-weight: 600;
          background: rgba(0,0,0,0.03);
        }
        .dark .tiptap-editor .editor-table th {
          background: rgba(255,255,255,0.05);
        }
        .tiptap-editor .editor-table td {
          background: transparent;
        }
        .tiptap-editor .editor-table .selectedCell {
          background: rgba(0,161,178,0.08);
          border-color: #00A1B2;
        }
        .dark .tiptap-editor .editor-table .selectedCell {
          background: rgba(16,185,129,0.08);
          border-color: #10b981;
        }
        .tiptap-editor .editor-table th.selectedCell {
          background: rgba(0,161,178,0.12);
        }
        .dark .tiptap-editor .editor-table th.selectedCell {
          background: rgba(16,185,129,0.12);
        }
        .tiptap-editor .editor-table tr:hover td {
          background: rgba(0,0,0,0.015);
        }
        .dark .tiptap-editor .editor-table tr:hover td {
          background: rgba(255,255,255,0.02);
        }
        .tiptap-editor .editor-table p {
          margin: 0;
        }
        /* Column resize handle */
        .tiptap-editor .column-resize-handle {
          position: absolute;
          right: -2px;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #00A1B2;
          cursor: col-resize;
          z-index: 20;
        }
        .dark .tiptap-editor .column-resize-handle {
          background: #10b981;
        }
        .tiptap-editor .tableWrapper {
          overflow-x: auto;
          margin: 1rem 0;
        }
      `}</style>

      {/* Image Picker (Phase 4) */}
      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onInsert={insertImageMarkdown}
        projectId={projectId}
      />
    </div>
  );
};
